import { APP_ROLE_PARAM_KEY, Role, rolesManipulator, useGetUserRoles, useLogout } from 'auth';
import { ComponentType, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { useAuth } from 'react-oidc-context';
import { useSearchParams } from 'react-router-dom';
import { getUserDefaultRoute } from '@/modules/auth/utils/defaultRoute.tsx';
import { Spinner } from 'ui';

export const persistentParams = [APP_ROLE_PARAM_KEY];

export const getPersistentParamsSearch = (currentRole?: Role) => {
  const res = new URLSearchParams();
  const currentParams = new URLSearchParams(location.search);

  persistentParams.forEach(param => {
    const value = currentParams.get(param);
    if (value) res.set(param, value);
  });

  const manipulatorRole = rolesManipulator.current_user_role;

  if (res.has(APP_ROLE_PARAM_KEY) && manipulatorRole) {
    res.set(APP_ROLE_PARAM_KEY, manipulatorRole);
  } else if (currentRole) {
    res.set(APP_ROLE_PARAM_KEY, currentRole);
  }
  return res;
};

export function withPrivateAccess<T = object>(Wrapped: ComponentType<T>, role: Role | Role[]) {
  return (props: T) => {
    const auth = useAuth();
    const logout = useLogout();
    const [params, setParams] = useSearchParams();

    const userRole = rolesManipulator.current_user_role;
    const isUserRoleValid = Object.values(Role).includes(userRole as Role);
    const userRoles = useGetUserRoles('compliance');
    const userHasRole = isUserRoleValid && userRoles.includes(userRole as Role);
    const isFetchingError = auth.error && auth.error.message === 'Failed to fetch';
    const redirectRole = userRole || userRoles?.[0];
    const shouldSigninRedirect = !isFetchingError && !auth.isLoading && !auth.isAuthenticated;
    const shouldRedirectToAppSelector =
      !auth.isLoading && auth.isAuthenticated && !userHasRole && !userRoles.length;
    const shouldSwitchRole =
      !auth.isLoading && auth.isAuthenticated && !userHasRole && !!userRoles.length;

    useEffect(() => {
      if (!isFetchingError) return;
      void logout(window.location.origin); // TODO: CHANGE IT (MAKE RETRIES IN OIDC LIBRARY)
    }, [isFetchingError, logout]);

    useEffect(() => {
      if (!shouldSigninRedirect) return;
      auth.signinRedirect({
        redirect_uri: `${location.origin}${location.pathname}?${getPersistentParamsSearch(
          redirectRole,
        ).toString()}`,
      });
    }, [auth, redirectRole, shouldSigninRedirect]);

    useEffect(() => {
      if (!shouldRedirectToAppSelector) return;
      window.open(import.meta.env.VITE_APP_SELECTOR_URL, '_self');
    }, [shouldRedirectToAppSelector]);

    useEffect(() => {
      if (!shouldSwitchRole) return;
      const nextParams = new URLSearchParams(params);
      nextParams.set(APP_ROLE_PARAM_KEY, userRoles[0]);
      setParams(nextParams, { replace: false });
    }, [params, setParams, shouldSwitchRole, userRoles]);

    if (
      (auth.isLoading && !auth.isAuthenticated) ||
      isFetchingError ||
      shouldSigninRedirect ||
      shouldRedirectToAppSelector ||
      shouldSwitchRole
    ) {
      return (
        <div className="flex h-full w-full items-center justify-center">
          <Spinner size="lg" />
        </div>
      );
    }

    const resolveRoles = !role || Array.isArray(role) ? role : [role];
    const userHasAccess = userRole && resolveRoles?.includes(userRole);

    if (!userHasAccess) {
      toast.error('You are not authorised to access this page', {
        id: 'access-denied',
      });
      return getUserDefaultRoute(userRole as Role);
    }

    return <Wrapped {...(props as any)} />;
  };
}
